import { CanceledError } from 'axios';
import i18next from 'i18next';
import _ from 'lodash';
import moment from 'moment';
import { isInsightBoardAvailable } from '@/actionBoards/insightBoardAvailability.utilities';
import {
  sqHomeScreenStore,
  sqLicenseManagementStore,
  sqWorkbenchStore,
  sqWorkbookStore,
  sqWorksheetStore,
} from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_WORKBENCH } from '@/core/flux.service';
import { isSystemTest } from '@/core/utilities';
import { formatTime } from '@/datetime/dateTime.utilities';
import { getPreferredTimeFormat } from '@/datetime/dateTimeFormatting.utilities';
import {
  HOME_SCREEN_CANCELLATION_GROUP,
  HOME_SCREEN_SORT,
  HOME_SCREEN_TABLE_TYPE,
  ITEM_TYPES,
  type SortByTableItem,
} from '@/homescreen/homescreen.constants';
import { getPageNumberForTable, getPageSizeByTable, getSortForTable } from '@/homescreen/homescreenState.utilities';
import { loadSearchTable } from '@/homescreen/legacy/legacyHomeScreen.actions';
import { getMeilisearchResults } from '@/homescreen/search/omniSearch.service';
import { DEFAULT_SEARCH_CONFIG } from '@/homescreen/search/omniSearch.utilities';
import { Feature } from '@/licenseManagement/licenseManagement.constants';
import { hasValidFeatureInLicense } from '@/licenseManagement/licenseManagement.utilities';
import { HOME_SCREEN_TABS, SEARCH_ITEM_LOCATIONS, USERS_FOLDER_NAME } from '@/main/app.constants';
import { getLoginLink, goTo, returnToPreviousState } from '@/main/routing.utilities';
import { cancelAll, cancelGroup } from '@/requests/pendingRequests.utilities';
import { canModifyWorkbook } from '@/services/authorization.service';
import { AUTH_CHANGE_BROADCAST_CHANNEL, subscribeToBroadcastChannel } from '@/services/broadcastChannel.utilities';
import { emitWorkbooks } from '@/services/notifier.service';
import {
  isNewHomeScreen2025Enabled,
  isNewHomeScreenWithMeilisearchEnabled,
  isVantageEnabled,
} from '@/services/systemConfiguration.utilities';
import { isCsrfSet } from '@/utilities/auth.utilities';
import {
  createFolder,
  createProject,
  createWorkbook,
  getDefaultFolderName,
  getFolder,
  getFolderRoot,
  getTabFolder,
  getTabFolderName,
  getWorkbook,
  setArchived,
} from '@/utilities/homescreen.utilities';
import { equalsIgnoreCase } from '@/utilities/utilities';
import { setJournalToOpen } from '@/workbench/workbench.actions';
import { addWorksheetToWorkbook as addWorksheetToWorkbookAction, loadWorkbook } from '@/workbook/workbook.actions';
import { WORKBOOK_DISPLAY } from '@/workbook/workbook.constants';
import {
  GetFoldersFilterEnum as FilterEnum,
  GetFoldersSearchFieldsEnum as SearchFieldsEnum,
  sqFoldersApi,
} from '@seeqdev/api/endpoint/FoldersApi';
import { SeeqNames } from '@seeqdev/constants';
import type { HomeScreenItemType } from '@/homescreen/HomeScreenTable.organism';
import type { TrainingPromptDisplay } from '@/homescreen/homescreen.constants';
import type { Breadcrumb, HomeScreenFolder, HomeScreenSearchParams } from '@/homescreen/homescreen.types';
import type { SearchItemLocation } from '@/main/app.constants';
import type { FolderNavigationOutputV1 } from '@seeqdev/api/model/FolderNavigationOutputV1';

type GetSubfoldersParams = Parameters<typeof sqFoldersApi.getSubfolders>[0];
type GetSubfoldersConfig = Parameters<typeof sqFoldersApi.getSubfolders>[1];

const SEARCH_LOCATION_TO_FILTER: Partial<Record<SearchItemLocation, FilterEnum>> = {
  [SEARCH_ITEM_LOCATIONS.ALL]: FilterEnum.All,
  [SEARCH_ITEM_LOCATIONS.USERS]: FilterEnum.Users,
  [SEARCH_ITEM_LOCATIONS.CORPORATE]: FilterEnum.Corporate,
  [SEARCH_ITEM_LOCATIONS.MY_FOLDER]: FilterEnum.Mine,
  [SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC]: FilterEnum.SharedOrPublic,
};

const SEARCH_FIELD_TO_ENUM: Record<string, SearchFieldsEnum | undefined> = {
  all: SearchFieldsEnum.All,
  name: SearchFieldsEnum.Name,
  description: SearchFieldsEnum.Description,
  document: SearchFieldsEnum.Document,
};

const isFilterEnumValue = (filter: unknown): filter is FilterEnum =>
  Object.values(FilterEnum).includes(filter as FilterEnum);

function getNavigationSubfolders(params: GetSubfoldersParams, config?: GetSubfoldersConfig) {
  if (
    params.folderId === SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC ||
    params.root === SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC
  ) {
    // getSimpleSubFolders does not support SHARED_OR_PUBLIC or performance reasons
    return sqFoldersApi.getSubfolders(params, config);
  }

  return sqFoldersApi.getSimpleSubfolders({ folderId: params.folderId }, config);
}

export const normalizeFilter = (filter?: HomeScreenSearchParams['filter']): FilterEnum | undefined => {
  if (!filter) {
    return undefined;
  }

  if (isFilterEnumValue(filter)) {
    return filter;
  }

  return SEARCH_LOCATION_TO_FILTER[filter];
};

export const normalizeTypes = (types?: HomeScreenSearchParams['types']): string[] | undefined => {
  if (!types) {
    return undefined;
  }

  if (types.length === 0) {
    return [];
  }

  const first = types[0];
  return Array.isArray(first) ? (types as string[][]).flat() : (types as string[]);
};

const normalizeSearchFields = (
  searchFields?: HomeScreenSearchParams['searchFields'],
): SearchFieldsEnum[] | undefined => {
  if (!searchFields) {
    return undefined;
  }

  const fields = Array.isArray(searchFields) ? searchFields : [searchFields];
  const mapped = fields
    .map((field) => SEARCH_FIELD_TO_ENUM[field as keyof typeof SEARCH_FIELD_TO_ENUM])
    .filter((field): field is SearchFieldsEnum => field !== undefined);

  return mapped.length > 0 ? mapped : undefined;
};

export function setCurrentTab(tab: HOME_SCREEN_TABS) {
  sqHomeScreenStore.actions.setCurrentTab(tab);
}

export function setPreviousTab(tab: HOME_SCREEN_TABS) {
  sqHomeScreenStore.actions.setPreviousTab(tab);
}

export function setCurrentFolder(folderId: string | null | undefined) {
  sqHomeScreenStore.actions.setCurrentFolderId(folderId);
}

export function addExpandedFolderIds(expandedFolderIds: string[]) {
  sqHomeScreenStore.actions.addExpandedFolderIds(expandedFolderIds);
}

export function removeExpandedFolderIds(collapsedFolderIds: string[]) {
  sqHomeScreenStore.actions.removeExpandedFolderIds(collapsedFolderIds);
}

export function mergeSubfolderTree(tree: FolderNavigationOutputV1[]) {
  sqHomeScreenStore.actions.mergeFolderTree(tree as HomeScreenFolder[]);
}

export function replaceFolderChildren(folderId: string, subfolders: FolderNavigationOutputV1[]) {
  sqHomeScreenStore.actions.replaceFolderChildren(folderId, subfolders as HomeScreenFolder[]);
}

export function setTableSort(sortProperty: string, sortAsc: boolean, table: string) {
  sqHomeScreenStore.actions.setTableSort(table, sortProperty, sortAsc);
}

export function clearBreadcrumbs() {
  sqHomeScreenStore.actions.clearBreadcrumbs();
}

export function clearItemsHomescreen() {
  sqHomeScreenStore.actions.clearItems();
}

export function setPageSizeForTable({ table, size }: { table?: string; size: number }) {
  sqHomeScreenStore.actions.setPageSizeForTable(table as string, size);
}

export function setSearchParams(searchParams: HomeScreenSearchParams) {
  sqHomeScreenStore.actions.setSearchParams(searchParams);
}

export function setIsExact(isExact: boolean) {
  sqHomeScreenStore.actions.setIsExact(isExact);
}

export function setPageNumber(pageNumber: number, table: string) {
  sqHomeScreenStore.actions.setPageNumberForTable(table, pageNumber);
}

export function getCancellationGroupForTable(table: string) {
  return `${HOME_SCREEN_CANCELLATION_GROUP}_${table}`;
}

export function setPageNumberAndGo(
  pageNumber: number,
  loadTable: (...args: unknown[]) => unknown,
  table?: string,
  searchParams?: HomeScreenSearchParams,
) {
  setPageNumber(pageNumber, table as string);
  loadTable(HOME_SCREEN_TABLE_TYPE.SEARCH ? searchParams : null);
}

/**
 * Copies active search filters from the store into the given searchParams.
 * Content-type filter is always preserved. Owner/creator filters are only preserved
 * when `preserveOwnerCreator` is true (i.e. the backend supports them for this table).
 */
function getCurrentSearchParams(
  searchParams: HomeScreenSearchParams,
  { preserveOwnerCreator = true }: { preserveOwnerCreator?: boolean } = {},
): HomeScreenSearchParams {
  const { types, ownerIds, creatorIds } = sqHomeScreenStore.getState().searchParams;
  let currentSearchParams = searchParams;

  if (types) currentSearchParams.types = types;

  if (preserveOwnerCreator) {
    if (ownerIds) currentSearchParams.ownerIds = ownerIds;
    if (creatorIds) currentSearchParams.creatorIds = creatorIds;
  }

  return currentSearchParams;
}

export function buildHomescreenSortOrder(
  sortProperty: string,
  sortAsc: boolean,
  { openedAtTiebreaker = false }: { openedAtTiebreaker?: boolean } = {},
): string {
  const direction = sortAsc ? 'asc' : 'desc';
  const primarySort = `${sortProperty} ${direction}`;

  if (openedAtTiebreaker && sortProperty !== HOME_SCREEN_SORT.OPENED_AT) {
    return `${primarySort}, ${HOME_SCREEN_SORT.OPENED_AT} ${direction}`;
  }

  return primarySort;
}

function getHomescreenSortOrder(
  sort?: SortByTableItem,
  options?: { openedAtTiebreaker?: boolean },
): string | undefined {
  if (!sort?.sortProperty) {
    return undefined;
  }

  return buildHomescreenSortOrder(sort.sortProperty, sort.sortAsc ?? true, options);
}

export function loadPinnedTable() {
  const table = HOME_SCREEN_TABLE_TYPE.PINNED;
  const sort = getSortForTable(sqHomeScreenStore.getState().sortByTable, table);
  const searchParams: HomeScreenSearchParams = getCurrentSearchParams({
    sortOrder: getHomescreenSortOrder(sort),
    onlyPinned: true,
  });

  return loadTable({ searchParams, table, cancellationGroup: getCancellationGroupForTable('pinned') });
}

export function loadRecentTable() {
  const table = HOME_SCREEN_TABLE_TYPE.RECENT;
  const sort = getSortForTable(sqHomeScreenStore.getState().sortByTable, table);
  const sortOrder = sort?.sortProperty
    ? getHomescreenSortOrder(sort, {
        openedAtTiebreaker: isNewHomeScreen2025Enabled(),
      })
    : buildHomescreenSortOrder(HOME_SCREEN_SORT.OPENED_AT, false);
  const searchParams: HomeScreenSearchParams = getCurrentSearchParams({ sortOrder });

  return loadTable({ searchParams, table, cancellationGroup: getCancellationGroupForTable('recent') });
}

type ResultEndpoint = 'meili' | 'folders' | 'navigate';

export function isCaptureOnlySelection(types: HomeScreenSearchParams['types']): boolean {
  const normalizedTypes = normalizeTypes(types);
  return normalizedTypes?.length === 1 && normalizedTypes[0] === ITEM_TYPES.CAPTURE;
}

function selectEndpoint(
  table: string,
  searchParams: { filter?: string; folderId?: string; isArchived?: boolean; types?: string[] | string[][] },
): ResultEndpoint {
  const meiliEnabled = isNewHomeScreenWithMeilisearchEnabled();
  const isSearch = table === HOME_SCREEN_TABLE_TYPE.SEARCH;
  const isTab = table === HOME_SCREEN_TABLE_TYPE.TAB;
  const isSharedTab = isTab && searchParams.filter === SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC;
  const isSharedRoot = isSharedTab && !searchParams.folderId;
  const isArchivedTab = isTab && Boolean(searchParams.isArchived);

  // 1) Search
  if (isSearch) return meiliEnabled ? 'meili' : 'folders';

  // Captures are not items, so only Meilisearch can list them in any tab
  if (isTab && meiliEnabled && isCaptureOnlySelection(searchParams.types)) return 'meili';

  // 2) Shared tab (all depths): never navigateFolders
  // root can use meili, subfolders use folders
  if (isSharedTab) {
    if (meiliEnabled && isSharedRoot) return 'meili';
    return 'folders';
  }

  // 3) Archived tab uses folders semantics
  if (isArchivedTab) return 'folders';

  // 4) Default tab navigation
  return 'navigate';
}

export function loadTabTable(
  options: {
    currentTab?: HOME_SCREEN_TABS;
    folderId?: string | null | undefined;
  } = {},
) {
  const table = HOME_SCREEN_TABLE_TYPE.TAB;
  const sort = getSortForTable(sqHomeScreenStore.getState().sortByTable, table);
  const currentTab = options.currentTab ?? sqHomeScreenStore.getState().currentTab;
  const folderId = options.folderId ?? sqHomeScreenStore.getState().currentFolderId;
  const initialSearchParams = getSearchParams({
    currentTab,
    folderId: folderId as string,
    sortProperty: sort?.sortProperty,
    sortAsc: sort?.sortAsc,
  });
  const searchParams = getCurrentSearchParams(initialSearchParams, {
    preserveOwnerCreator: isNewHomeScreenWithMeilisearchEnabled(),
  });

  return loadTable({
    searchParams,
    table,
    currentTab,
    cancellationGroup: getCancellationGroupForTable('tab'),
  });
}

// Replaced loads may still settle after their replacement has started or completed.
const activeTableLoads = new Map<string, symbol>();

export async function loadTable({
  searchParams,
  table,
  currentTab,
  isCurrentFolderNested = false,
  advancedSearchSelectedFolder = '',
  locationChangedInAdvanced = false,
  breadcrumbClicked = false,
  cancellationGroup,
}: {
  searchParams: HomeScreenSearchParams;
  table: string;
  currentTab?: HOME_SCREEN_TABS;
  isCurrentFolderNested?: boolean;
  advancedSearchSelectedFolder?: string;
  locationChangedInAdvanced?: boolean;
  breadcrumbClicked?: boolean;
  cancellationGroup: string;
}) {
  const loadToken = Symbol();
  activeTableLoads.set(table, loadToken);
  const isCurrentLoad = () => activeTableLoads.get(table) === loadToken;
  sqHomeScreenStore.actions.setTableLoading(table, true);
  sqHomeScreenStore.actions.setIsLoadingBreadcrumbs(true);
  const finishLoading = () => {
    if (!isCurrentLoad()) return;
    activeTableLoads.delete(table);
    sqHomeScreenStore.actions.setTableLoading(table, false);
    if (activeTableLoads.size === 0) {
      sqHomeScreenStore.actions.setIsLoadingBreadcrumbs(false);
    }
  };

  const limit = getPageSizeByTable(sqHomeScreenStore.getState().pageSizeByTable, table);
  const currentPageNumber = getPageNumberForTable(sqHomeScreenStore.getState().pageNumberByTable, table);
  const folderId =
    searchParams.folderId ||
    (!isCurrentFolderNested && advancedSearchSelectedFolder !== '') ||
    (advancedSearchSelectedFolder === '' && (locationChangedInAdvanced || breadcrumbClicked))
      ? searchParams.folderId
      : sqHomeScreenStore.getState().currentFolderId;

  const getFolders = async (limit: number, pageNumber: number) => {
    let filter = searchParams.filter;
    if (folderId) {
      // Same group as the listing so a navigation check preserves this lookup along with it (CRAB-62264)
      await getFolder(folderId, { cancellationGroup }).then((folder) => {
        if (folder?.name === USERS_FOLDER_NAME) {
          filter = FilterEnum.Users;
        }
      });
    }

    // getFolder can finish after a newer load has started. Do not cancel that newer load's request.
    if (!isCurrentLoad()) throw new CanceledError('Home screen load was superseded');

    const offset = (pageNumber - 1) * limit;
    const queryParams = {
      ...searchParams,
      filter: normalizeFilter(filter),
      types: normalizeTypes(searchParams.types),
      searchFields: normalizeSearchFields(searchParams.searchFields),
      folderId,
      limit,
      offset,
    };

    const includeActionBoards = isInsightBoardAvailable();
    queryParams.types ||= [
      SeeqNames.Types.Folder,
      SeeqNames.Types.Analysis,
      SeeqNames.Types.Topic,
      SeeqNames.Types.Project,
      includeActionBoards ? SeeqNames.Types.ActionBoard : undefined,
      hasValidFeatureInLicense(sqLicenseManagementStore.getState().license, Feature.Vantage) && isVantageEnabled()
        ? SeeqNames.Types.Vantage
        : undefined,
    ].filter(Boolean) as string[];
    cancelGroup(cancellationGroup);
    const { data } = await sqFoldersApi.getFolders(queryParams, { cancellationGroup });

    return _.pick(data, ['content', 'totalResults', 'path']);
  };

  const getNavigateFolders = async (limit: number, pageNumber: number) => {
    const offset = (pageNumber - 1) * limit;
    const includeActionBoards = isInsightBoardAvailable();
    const types =
      normalizeTypes(searchParams.types) ||
      ([
        SeeqNames.Types.Folder,
        SeeqNames.Types.Analysis,
        SeeqNames.Types.Topic,
        SeeqNames.Types.Project,
        includeActionBoards ? SeeqNames.Types.ActionBoard : undefined,
        hasValidFeatureInLicense(sqLicenseManagementStore.getState().license, Feature.Vantage) && isVantageEnabled()
          ? SeeqNames.Types.Vantage
          : undefined,
      ].filter(Boolean) as string[]);

    const isTabTable = table === HOME_SCREEN_TABLE_TYPE.TAB;
    const queryParams = {
      isArchived: Boolean(searchParams.isArchived),
      sortOrder: searchParams.sortOrder,
      offset,
      limit,
      onlyPinned: Boolean(searchParams.onlyPinned),
      folderId: isTabTable ? folderId || undefined : undefined,
      root: isTabTable ? getFolderRoot(currentTab ?? sqHomeScreenStore.getState().currentTab) : undefined,
      types,
    };

    cancelGroup(cancellationGroup);
    const { data } = await sqFoldersApi.navigateFolders(queryParams, { cancellationGroup });
    return _.pick(data, ['content', 'totalResults', 'path']);
  };

  const executeMeilisearch = async (limit: number, pageNumber: number) => {
    const result = await getMeilisearchResults({
      params: searchParams,
      config: {
        ...DEFAULT_SEARCH_CONFIG,
        resultsPerPage: limit,
        page: pageNumber,
      },
      cancellationGroup,
    });

    if (result.error) {
      return { content: [], totalResults: 0 };
    } else {
      return {
        content: result.results,
        path: result.path,
        totalResults: result.totalResults,
      };
    }
  };

  const endpoint = selectEndpoint(table, searchParams);
  const getSearchResults =
    endpoint === 'meili' ? executeMeilisearch : endpoint === 'folders' ? getFolders : getNavigateFolders;

  return getSearchResults(limit, currentPageNumber)
    .then((data) => {
      if (!isCurrentLoad()) return data;
      if (_.isEmpty(data.content) && currentPageNumber > 1) {
        // This can happen when items were deleted from the current page, and there are no more items to display
        // for it.  We simply re-fetch by setting the page to the previous one
        setPageNumber(currentPageNumber - 1, table);
        return getSearchResults(limit, currentPageNumber - 2);
      }
      return data;
    })
    .then((data) => {
      if (!isCurrentLoad()) return;
      const normalizedContent = Array.isArray(data.content)
        ? data.content
        : data.content
          ? _.castArray(data.content)
          : [];

      sqHomeScreenStore.actions.setItemsForTable(table, normalizedContent as HomeScreenItemType[], data?.totalResults);
      if (data?.path) {
        sqHomeScreenStore.actions.setBreadcrumbs(data.path as unknown as Breadcrumb[]);
      } else {
        clearBreadcrumbs();
      }
    })
    .finally(finishLoading);
}

export function loadFolderTree(folderId: string | null | undefined, currentTab: HOME_SCREEN_TABS | null) {
  let tabFolderPromise;
  let expandToFolder = false;

  // Check to see if folder is actually a tab,then use it. Every request here belongs to the Home cancellation group
  // so that a navigation check leaving a worksheet preserves them along with the table load (CRAB-62264)
  const homeRequest = { cancellationGroup: HOME_SCREEN_CANCELLATION_GROUP };
  tabFolderPromise = getTabFolderName(folderId, homeRequest);

  return tabFolderPromise.then((tabFolder): any => {
    const newTab = _.isUndefined(tabFolder) ? currentTab : tabFolder;
    const folderRoot = getFolderRoot(newTab);
    let promise;

    if (folderId && _.isUndefined(tabFolder)) {
      expandToFolder = true;
      promise = sqFoldersApi.getAncestors({ folderId, root: folderRoot }, homeRequest);
    } else if (folderRoot) {
      promise = getNavigationSubfolders({ folderId: folderRoot }, homeRequest);
      // folderRoot is only set for a real tab, so newTab is never null here
      if (newTab && newTab !== sqHomeScreenStore.getState().currentTab) {
        clearItemsHomescreen();
        setCurrentTab(newTab);
      }
    } else {
      return Promise.resolve({});
    }
    return promise.then(({ data: { id, subfolders } }) => {
      mergeSubfolderTree(subfolders ?? []);

      if (expandToFolder) {
        if (!folderRoot && id) {
          if (id === SEARCH_ITEM_LOCATIONS.MY_FOLDER) {
            setCurrentTab(HOME_SCREEN_TABS.MY_FOLDER);
          } else if (id === SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC) {
            if (currentTab !== HOME_SCREEN_TABS.SHARED) {
              setCurrentTab(HOME_SCREEN_TABS.SHARED);
              // CRAB-29112: The SHARED folder has special logic regarding how items are shown users so we must load
              // the folder with the `currentTab` correctly set
              clearItemsHomescreen();
              return loadFolder(folderId, HOME_SCREEN_TABS.SHARED).then(() =>
                addExpandedFolderIds(_.map(sqHomeScreenStore.getState().breadcrumbs, 'id')),
              );
            }
          } else if (id === SEARCH_ITEM_LOCATIONS.CORPORATE) {
            setCurrentTab(HOME_SCREEN_TABS.CORPORATE);
          } else if (id === SEARCH_ITEM_LOCATIONS.USERS) {
            setCurrentTab(HOME_SCREEN_TABS.USERS);
          } else if (id === SEARCH_ITEM_LOCATIONS.EXTERNALLY_SHARED) {
            setCurrentTab(HOME_SCREEN_TABS.EXTERNALLY_SHARED);
          }
        }
      } else if (folderRoot) {
        // Set the tab folder as the current folder for navigable folder roots (not filter-based tab roots).
        if (folderRoot === SEARCH_ITEM_LOCATIONS.MY_FOLDER) {
          getTabFolder(HOME_SCREEN_TABS.MY_FOLDER, homeRequest).then((folder: { id: string }) => {
            setCurrentFolder(folder.id);
          });
        } else if (folderRoot === SEARCH_ITEM_LOCATIONS.CORPORATE) {
          getTabFolder(HOME_SCREEN_TABS.CORPORATE, homeRequest).then((folder: { id: string }) => {
            setCurrentFolder(folder.id);
          });
        } else if (folderRoot === SEARCH_ITEM_LOCATIONS.EXTERNALLY_SHARED) {
          getTabFolder(HOME_SCREEN_TABS.EXTERNALLY_SHARED, homeRequest).then((folder: { id: string } | undefined) => {
            if (folder) {
              setCurrentFolder(folder.id);
            }
          });
        }
      }
    });
  });
}

export function loadHomeTab() {
  return Promise.all([loadPinnedTable(), loadRecentTable()]);
}

export function loadTab(currentTab: HOME_SCREEN_TABS, folderId: string | null | undefined) {
  const finalizeTabLoad = (resolvedTab: HOME_SCREEN_TABS) => {
    addExpandedFolderIds(_.map(sqHomeScreenStore.getState().breadcrumbs, 'id'));
    const tabRootFolderId = sqHomeScreenStore.getState().tabFolders?.[resolvedTab ?? '']?.id ?? null;
    const requestedFolderId = folderId ?? null;
    const isTabRootFolder =
      requestedFolderId !== null && tabRootFolderId !== null && equalsIgnoreCase(requestedFolderId, tabRootFolderId);

    if (requestedFolderId !== null) {
      const root = getFolderRoot(resolvedTab);
      getNavigationSubfolders(
        { folderId: requestedFolderId, root },
        { cancellationGroup: HOME_SCREEN_CANCELLATION_GROUP },
      ).then((response) => {
        const folder = response.data;
        const isCurrentFolderNode = folder?.id && equalsIgnoreCase(folder.id, requestedFolderId);

        if (isCurrentFolderNode && isTabRootFolder) {
          // root already represented by top-level children
          replaceFolderChildren(folder.id, folder.subfolders ?? []);
          return;
        }

        if (isCurrentFolderNode) {
          replaceFolderChildren(folder.id, folder.subfolders ?? []);
          return;
        }

        mergeSubfolderTree([folder]);
      });
    }
  };

  // Direct folder URL navigation starts with HOME as currentTab. Resolve the actual tab first so tab-table loading
  // sends the correct root for breadcrumb routing.
  if (currentTab === HOME_SCREEN_TABS.HOME && folderId) {
    return loadFolderTree(folderId, currentTab).then(() => {
      const resolvedTab = sqHomeScreenStore.getState().currentTab;
      return loadTabTable({ currentTab: resolvedTab, folderId }).then(() => finalizeTabLoad(resolvedTab));
    });
  }

  return Promise.all([loadTabTable({ currentTab, folderId }), loadFolderTree(folderId, currentTab)]).then(() =>
    finalizeTabLoad(currentTab),
  );
}

export function getSearchParams({
  currentTab,
  folderId,
  sortProperty,
  sortAsc,
}: {
  currentTab: HOME_SCREEN_TABS;
  folderId: string;
  sortProperty?: string | null;
  sortAsc?: boolean | null;
}): HomeScreenSearchParams {
  const searchParams: HomeScreenSearchParams = {};
  if (sortProperty) {
    searchParams.sortOrder = `${sortProperty} ${(sortAsc ?? true) ? 'asc' : 'desc'}`;
  }
  if (folderId) {
    let usersFilter = currentTab === HOME_SCREEN_TABS.USERS ? SEARCH_ITEM_LOCATIONS.USERS : undefined;
    let sharedFilter = currentTab === HOME_SCREEN_TABS.SHARED ? SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC : undefined;
    return _.assign(searchParams, {
      folderId,
      filter: sharedFilter ?? usersFilter,
    });
  } else {
    switch (currentTab) {
      case HOME_SCREEN_TABS.USERS:
        return _.assign(searchParams, {
          folderId: SEARCH_ITEM_LOCATIONS.USERS,
        });
      case HOME_SCREEN_TABS.MY_FOLDER:
        return _.assign(searchParams, {
          folderId: SEARCH_ITEM_LOCATIONS.MY_FOLDER,
        });
      case HOME_SCREEN_TABS.SHARED:
        return _.assign(searchParams, {
          filter: SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC,
        });
      case HOME_SCREEN_TABS.CORPORATE:
        return _.assign(searchParams, {
          folderId: SEARCH_ITEM_LOCATIONS.CORPORATE,
        });
      case HOME_SCREEN_TABS.EXTERNALLY_SHARED:
        return _.assign(searchParams, {
          folderId: SEARCH_ITEM_LOCATIONS.EXTERNALLY_SHARED,
        });
      case HOME_SCREEN_TABS.TRASH:
        return _.assign(searchParams, { isArchived: true });
      default:
        return searchParams;
    }
  }
}

export function loadFolder(
  folderId?: string | null,
  currentTab?: HOME_SCREEN_TABS,
  options?: { forceClear?: boolean },
): Promise<{ content?: HomeScreenItemType[] }> {
  cancelAll();
  currentTab = currentTab || HOME_SCREEN_TABS.HOME;
  const shouldClear = options?.forceClear === true || currentTab !== sqHomeScreenStore.getState().currentTab;

  if (shouldClear) {
    clearItemsHomescreen();
  }

  // the API returns a magic string for the "root" tab of an item, so that we can show the selected tab without
  // encoding it in the URL.
  // Items can appear in more than one "tab". For example: my folder A is My Folder, but it's also under my home
  // folder in the Users tab.
  // Tabs are not persisted in the store between reloads - so if we have no currentTab we use the one the API
  // returns, if we have a tab we use that, as to avoid a jumpy tab selection.
  if (
    currentTab !== sqHomeScreenStore.getState().currentTab ||
    (!(_.isNil(folderId) && _.isNil(sqHomeScreenStore.getState().currentFolderId)) &&
      !equalsIgnoreCase(folderId, sqHomeScreenStore.getState().currentFolderId))
  ) {
    setCurrentTab(currentTab);
    setCurrentFolder(folderId);
  }

  if (!folderId && currentTab === HOME_SCREEN_TABS.HOME) {
    // Captures are never recently opened or pinned, so Home lists them the way the other tabs do
    return (
      isCaptureOnlySelection(sqHomeScreenStore.getState().searchParams.types)
        ? loadTabTable({ currentTab })
        : loadHomeTab()
    ) as Promise<any>;
  } else {
    return loadTab(currentTab, folderId) as Promise<any>;
  }
}

export function collapseFolder(folderId: string) {
  removeExpandedFolderIds([folderId]);
}

export function getNextTableSort(
  sortProperty: string,
  tableSort?: SortByTableItem,
): { sortProperty: string | null; sortAsc: boolean | null } {
  if (tableSort?.sortProperty !== sortProperty) {
    return { sortProperty, sortAsc: true };
  }

  if (tableSort.sortAsc) {
    return { sortProperty, sortAsc: false };
  }

  return { sortProperty: null, sortAsc: null };
}

export function getSortedTableContents(sortProperty: string, table: string) {
  const tableSort = getSortForTable(sqHomeScreenStore.getState().sortByTable, table);
  const { sortProperty: nextSortProperty, sortAsc } = getNextTableSort(sortProperty, tableSort);
  setPageNumber(1, table);

  sqHomeScreenStore.actions.setTableSort(table, nextSortProperty, sortAsc);

  switch (table) {
    case HOME_SCREEN_TABLE_TYPE.PINNED:
      return loadPinnedTable();
    case HOME_SCREEN_TABLE_TYPE.RECENT:
      return loadRecentTable();
    case HOME_SCREEN_TABLE_TYPE.SEARCH:
      return loadSearchTable({});
    default:
      return loadTabTable();
  }
}

// open a folder navigation tree but don't load the folder contents
export function expandFolder(folderId: string, expandedFolderIds: string[], folderRoot: string | null = null) {
  addExpandedFolderIds(_.uniq([...expandedFolderIds, folderId]));
  getNavigationSubfolders({ folderId, root: folderRoot ?? undefined }).then((response) => {
    mergeSubfolderTree([response.data]);
  });
}

export function resetStore() {
  sqHomeScreenStore.actions.resetSearchAndPagination();
}

export function clearSearchResults() {
  sqHomeScreenStore.actions.setItemsForTable(HOME_SCREEN_TABLE_TYPE.SEARCH, []);
}

export function addFolder({
  parentFolderId,
  branchFrom,
  name,
  ownerId,
}: {
  parentFolderId?: string;
  branchFrom?: string;
  name?: string;
  ownerId?: string;
}) {
  const folderName = name ? name : getDefaultFolderName();

  return canCreateItemInFolder(parentFolderId).then((folderWriteAccess) => {
    setTableSort(HOME_SCREEN_SORT.CREATED_AT, false, HOME_SCREEN_TABLE_TYPE.TAB);
    return createFolder(
      folderName,
      folderWriteAccess && parentFolderId ? parentFolderId : null,
      branchFrom,
      ownerId,
    ).then((folder) => {
      setCurrentFolder(folder.parentFolderId);
      return folder;
    });
  });
}

export function addWorkbook({
  name = '',
  addNewWorksheet = true,
  isTopic = false,
  isVantage = false,
  isActionBoard = false,
  branchFrom,
  folderId,
}: {
  name?: string;
  addNewWorksheet?: boolean;
  isTopic?: boolean;
  isVantage?: boolean;
  isActionBoard?: boolean;
  branchFrom?: string;
  folderId?: string | null;
}) {
  const prefix = i18next.t(
    `ITEM_TYPES.${(isTopic && 'REPORT') || (isVantage && 'VANTAGE') || (isActionBoard && 'ACTIONBOARD') || 'WORKBOOK'}`,
  );
  const worksheetPrefix =
    (isTopic && i18next.t('ITEM_TYPES.DOCUMENT')) ||
    (isVantage && i18next.t('ITEM_TYPES.VIEW')) ||
    (isActionBoard && i18next.t('ITEM_TYPES.BOARD')) ||
    '';
  moment.locale(_.truncate(sqWorkbenchStore.userLanguage, { length: 2, omission: '' }));
  const workbookTimeFormat = getPreferredTimeFormat(sqWorkbenchStore.use24HourFormat, 'lll');
  name = name || `${prefix} ${formatTime(new Date(), sqWorksheetStore.timezone, workbookTimeFormat)}`;

  return canCreateItemInFolder(folderId)
    .then((folderWriteAccess) =>
      createWorkbook(
        name,
        branchFrom,
        folderWriteAccess ? folderId : null,
        (isTopic && 'Topic') || (isVantage && 'Vantage') || (isActionBoard && 'ActionBoard') || 'Analysis',
      ),
    )
    .then((workbook: any) => {
      if (addNewWorksheet && !isSystemTest()) {
        setJournalToOpen(null);
      }
      if (addNewWorksheet && !branchFrom) {
        if (sqWorkbookStore.workbookId !== workbook.workbookId) {
          return loadWorkbook(workbook.workbookId, WORKBOOK_DISPLAY.EDIT).then(() =>
            addWorksheetToWorkbook(workbook.workbookId, {
              prefix: worksheetPrefix,
            }),
          );
        } else {
          return addWorksheetToWorkbook(workbook.workbookId, {
            prefix: worksheetPrefix,
          });
        }
      } else if (branchFrom) {
        return getWorkbook(workbook.workbookId);
      } else {
        return workbook;
      }
    })
    .then((workbook) => _.tap(workbook, emitWorkbooks));
}

export function addProject({ folderId = null, name = null }: { folderId?: string | null; name?: string | null } = {}) {
  const prefix = i18next.t('ITEM_TYPES.PROJECT');
  const projectTimeFormat = getPreferredTimeFormat(sqWorkbenchStore.use24HourFormat, 'lll');
  const projectName = name || `${prefix} ${formatTime(new Date(), sqWorksheetStore.timezone, projectTimeFormat)}`;

  return canCreateItemInFolder(folderId ?? undefined)
    .then((folderWriteAccess) => createProject(projectName, folderWriteAccess ? folderId : null))
    .then((project) => {
      flux.dispatch('WORKBENCH_ADD_PROJECT', project);
      emitWorkbooks();
      return project;
    });
}

export function addWorksheetToWorkbook(workbookId: string, options?: { prefix?: string }) {
  return addWorksheetToWorkbookAction(workbookId, options).then((worksheet) => {
    // addWorksheet sets the worksheet into the store and this makes the object immutable (freezes the object)
    // to add workbookId on the result we need to work with a clone
    return _.set(_.cloneDeep(worksheet), 'workbookId', workbookId);
  });
}

export function canCreateItemInFolder(folderId?: string | null) {
  const resolvedFolderId = folderId ?? sqHomeScreenStore.getState().currentFolderId;

  if (resolvedFolderId) {
    return getFolder(resolvedFolderId).then((folder) => canModifyWorkbook(folder));
  }

  return Promise.resolve(true);
}

export function removeWorkbook(workbook: { workbookId: string }) {
  return Promise.resolve()
    .then(() => setArchived(workbook.workbookId, true))
    .then((workbook) => _.tap(workbook, emitWorkbooks));
}

export function restoreWorkbook(workbook: { workbookId: string }) {
  return Promise.resolve()
    .then(() => setArchived(workbook.workbookId, false))
    .then((workbook) => _.tap(workbook, emitWorkbooks));
}

export function subscribeToAuthChangeChannel() {
  // Redirect to the logout page if auth changed by another tab or redirect from the login page if authenticated
  subscribeToBroadcastChannel({
    channelId: AUTH_CHANGE_BROADCAST_CHANNEL,
    onMessage() {
      // The csrf is used as a proxy for authentication here if the other tab set the csrf then it is likely
      // that we are authenticated. Similarly, if the csrf has been cleared we are unauthenticated.
      if (isCsrfSet() && _.includes(window.location.pathname, '/login')) {
        returnToPreviousState();
      } else if (!isCsrfSet() && !_.includes(window.location.pathname, '/login')) {
        goTo(getLoginLink(window.location.pathname + window.location.search));
      }
    },
  });
}

export function setTabFolder(key: string, folder: { id: string; name: string }) {
  sqHomeScreenStore.actions.setTabFolder(key, folder);
}

export function setTrainingPromptDisplay(trainingPromptDisplay: TrainingPromptDisplay) {
  sqHomeScreenStore.actions.setTrainingPromptDisplay(trainingPromptDisplay);
}

export function setDisplayedAddOnIdentifier(displayedAddOnIdentifier: string) {
  sqHomeScreenStore.actions.setDisplayedAddOnIdentifier(displayedAddOnIdentifier);
}

export function clearDisplayedAddOnIdentifier() {
  setDisplayedAddOnIdentifier('');
}

export function setCustomLinksExpanded(customLinksExpanded: boolean) {
  sqHomeScreenStore.actions.setCustomLinksExpanded(customLinksExpanded);
}

export function setBranchCode(branchCode: string) {
  sqHomeScreenStore.actions.setBranchCode(branchCode);
}

export function setShowOfficeHours(showOfficeHours: boolean) {
  flux.dispatch('SET_SHOW_OFFICE_HOURS', { showOfficeHours }, PUSH_WORKBENCH);
}
